import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { TrendingUp, Loader2, Target, Trophy, Info } from "lucide-react";

const SENS = { low: 7, med: 12, high: 20 };
const pct = (n) => `${Math.round(n * 100)}%`;

export default function WinTab({ projectId }) {
  const { data: boq, isLoading } = useQuery({ queryKey: ["boq", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/boq`)).data });
  const t = boq?.totals || {};
  const grand = Number(t.grand_total) || 0;
  const profit = Number(t.profit_amount) || 0;
  const cost = Math.max(0, grand - profit); // everything except profit

  const [market, setMarket] = useState(null);
  const [bidders, setBidders] = useState(3);
  const [sens, setSens] = useState("med");

  const M = market === null ? grand : (Number(market) || grand);
  const s = SENS[sens];
  const N = Math.max(1, Number(bidders) || 1);

  const winAt = (P) => (M > 0 ? 1 / (1 + (N - 1) * Math.exp((s * (P - M)) / M)) : 0);

  const rows = useMemo(() => {
    if (!grand) return [];
    const lo = Math.max(cost, M * 0.85);
    const hi = Math.max(lo * 1.02, M * 1.2);
    const steps = 13;
    const out = [];
    for (let i = 0; i < steps; i++) {
      const P = lo + ((hi - lo) * i) / (steps - 1);
      const w = winAt(P);
      const pf = P - cost;
      out.push({ P, win: w, profit: pf, margin: P > 0 ? pf / P : 0, ev: w * pf });
    }
    return out;
  }, [grand, cost, M, s, N]); // eslint-disable-line

  const best = useMemo(() => rows.filter((r) => r.profit > 0).reduce((a, b) => (b.ev > (a?.ev ?? -Infinity) ? b : a), null), [rows]);
  const maxWin = Math.max(...rows.map((r) => r.win), 0.0001);
  const curWin = winAt(grand);
  const curMargin = grand > 0 ? (grand - cost) / grand : 0;

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!grand) return <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground text-sm">Add priced line items to your estimate to model win probability.</div>;

  return (
    <div className="space-y-6" data-testid="win-tab">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Win Probability &amp; Competitive Positioning</h3>
        <p className="text-xs text-muted-foreground">A deterministic model (no AI / no credits): win odds, profit and expected value at different bid prices, relative to the market.</p>
      </div>

      {/* Controls */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Estimated market average bid</div>
          <div className="flex items-center rounded-lg border border-input bg-popover px-2">
            <span className="text-muted-foreground text-sm">$</span>
            <input data-testid="win-market-input" type="number" value={market === null ? Math.round(grand) : market} onChange={(e) => setMarket(e.target.value)}
              className="w-full bg-transparent px-1 py-2 text-sm font-mono outline-none" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Default = your current bid. Lower it if competitors are cheaper.</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Competing bidders: {N}</div>
          <input data-testid="win-bidders-input" type="range" min="1" max="8" value={N} onChange={(e) => setBidders(e.target.value)} className="w-full accent-primary" />
          <div className="text-[11px] text-muted-foreground mt-1">More bidders → lower odds at the same price.</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Price sensitivity</div>
          <div className="flex rounded-lg border border-border overflow-hidden text-sm" data-testid="win-sensitivity">
            {["low", "med", "high"].map((k) => (
              <button key={k} data-testid={`win-sens-${k}`} onClick={() => setSens(k)}
                className={`flex-1 px-2 py-1.5 font-semibold capitalize ${sens === k ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{k}</button>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">How strongly price drives the award.</div>
        </div>
      </div>

      {/* Headline cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="win-current">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Your current bid</div>
          <div className="text-2xl font-mono font-bold tabular-nums">{money(grand, 0)}</div>
          <div className="text-sm mt-1"><span className="text-primary font-bold">{pct(curWin)}</span> win · {pct(curMargin)} margin</div>
        </div>
        {best && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="win-optimal">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-500 flex items-center gap-1"><Trophy className="h-3 w-3" /> Max expected value</div>
            <div className="text-2xl font-mono font-bold tabular-nums text-emerald-500">{money(best.P, 0)}</div>
            <div className="text-sm mt-1">{pct(best.win)} win · {pct(best.margin)} margin · EV {money(best.ev, 0)}</div>
          </div>
        )}
        <div className="rounded-xl border border-border bg-card p-4" data-testid="win-cost">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Target className="h-3 w-3" /> Cost floor (break-even)</div>
          <div className="text-2xl font-mono font-bold tabular-nums">{money(cost, 0)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">All costs except profit. Bidding below this loses money.</div>
        </div>
      </div>

      {/* Price ladder */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Bid price ladder — win %, margin &amp; expected value</div>
        <div className="space-y-1.5" data-testid="win-ladder">
          {rows.map((r, i) => {
            const isBest = best && Math.abs(r.P - best.P) < 0.01;
            return (
              <div key={i} className={`grid grid-cols-[88px_1fr_120px] items-center gap-3 rounded-lg px-2 py-1.5 ${isBest ? "bg-emerald-500/10 border border-emerald-500/30" : ""}`} data-testid={`win-row-${i}`}>
                <span className="font-mono text-sm tabular-nums">{money(r.P, 0)}</span>
                <div className="h-4 rounded bg-secondary overflow-hidden relative">
                  <div className="h-full rounded transition-all" style={{ width: `${(r.win / maxWin) * 100}%`, background: isBest ? "#10B981" : "hsl(var(--primary))" }} />
                  <span className="absolute inset-0 flex items-center px-2 text-[11px] font-mono font-semibold">{pct(r.win)} win</span>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground text-right tabular-nums">{pct(r.margin)} · EV {money(r.ev, 0)}</span>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Expected value = win probability × profit. The highlighted row maximises expected value for your inputs — a guide, not a guarantee.</p>
      </div>
    </div>
  );
}
